import * as React from "react";
import {
  ArrowLeft,
  ArrowLeftIcon,
  Check,
  ChevronsUpDown,
  PlusCircle,
} from "lucide-react";
import { motion, AnimatePresence } from "motion/react";

import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { toTitleCase } from "typed-case";
import { isValidElement } from "react";

export interface ComboboxItem {
  value: string;
  label: React.ReactNode;
}

export interface CreateItemProps {
  input: string;
  onSelect: () => void;
}

const DefaultCreateItem = ({ input, onSelect }: CreateItemProps) => (
  <CommandItem
    value={`create-${input}`}
    className="text-sm flex items-center gap-2 text-primary"
    onSelect={onSelect}
  >
    <PlusCircle className="h-4 w-4" />
    Create "{input}"
  </CommandItem>
);

export type ComboboxCreateItemFormProps<T extends ComboboxItem> = {
  input: string;
  onSubmit: (value: string, item: T) => void;
  onCancel: () => void;
};

interface CreateableComboboxProps<T extends ComboboxItem> {
  items: T[];
  placeholder?: string;
  itemName?: string;
  value?: string | null;
  onChange: (value: string, item: T) => void;
  onCreateItem?: (input: string, item: T) => void;
  canCreateItem?: (input: string) => boolean;
  CreateItemComponent?: React.ComponentType<CreateItemProps>;
  CreateItemFormComponent: React.ComponentType<ComboboxCreateItemFormProps<T>>;
  buttonClassName?: string;
  popoverWidth?: string;
}

export default function CreateableCombobox<T extends ComboboxItem>({
  items,
  placeholder = "Select an item...",
  itemName = "item",
  value,
  onChange,
  onCreateItem,
  CreateItemFormComponent,
  canCreateItem = () => true,
  CreateItemComponent = DefaultCreateItem,
  buttonClassName = "w-[200px]",
  popoverWidth = "w-[200px]",
}: CreateableComboboxProps<T>) {
  const [open, setOpen] = React.useState(false);
  const [inputValue, setInputValue] = React.useState("");
  const [isCreatingItem, setIsCreatingItem] = React.useState(false);

  const selectedItem = items.find((item) => item.value === value);

  // Determine if we should show the create option
  const showCreateOption = React.useMemo(() => {
    if (!onCreateItem) return false;

    // Only check if the input exactly matches an existing item
    const exactMatch = items.some(
      (item) => item.value.toLowerCase() === inputValue.toLowerCase(),
    );
    if (exactMatch) return false;

    return canCreateItem(inputValue);
  }, [inputValue, items, onCreateItem, canCreateItem]);

  // Handle entering create item mode
  const handleEnterCreateMode = React.useCallback(() => {
    if (onCreateItem) {
      setIsCreatingItem(true);
      setOpen(false);
    }
  }, [onCreateItem]);

  // Handle item creation submission
  const handleCreateSubmit = React.useCallback(
    (finalValue: string, item: T) => {
      if (onCreateItem) {
        onCreateItem(finalValue, item);
        onChange(finalValue, item);
        setOpen(false);
        setInputValue("");
        setIsCreatingItem(false);
      }
    },
    [onCreateItem],
  );

  // Handle canceling creation
  const handleCreateCancel = React.useCallback(() => {
    setIsCreatingItem(false);
  }, []);

  // Inline panel animation variants
  const panelVariants = {
    collapsed: { height: 0, opacity: 0 },
    open: { height: "auto", opacity: 1 },
  } as const;

  return (
    <div className={cn("w-full", buttonClassName)}>
      <div className="inline-flex w-full">
        <Popover
          open={open}
          onOpenChange={(newOpen) => {
            if (!newOpen) {
              setIsCreatingItem(false);
            }
            setOpen(newOpen);
          }}
        >
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              role="combobox"
              aria-expanded={open}
              className={cn(
                "justify-between flex-1 rounded-r-none truncate",
                isCreatingItem && "rounded-b-none",
              )}
              title={selectedItem ? reactNodeToString(selectedItem.label) : ""}
            >
              {selectedItem ? selectedItem.label : placeholder}
              <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
            </Button>
          </PopoverTrigger>
          <PopoverContent
            className={cn("p-0 overflow-hidden", popoverWidth)}
            align="start"
          >
            <Command>
              <CommandInput
                placeholder={`Search ${itemName.toLowerCase()}...`}
                className="h-9"
                value={inputValue}
                onValueChange={setInputValue}
              />
              <CommandList>
                <CommandGroup>
                  {items.map((item) => (
                    <CommandItem
                      key={item.value}
                      value={item.value}
                      keywords={[reactNodeToString(item.label)]}
                      onSelect={(currentValue) => {
                        const selectedItem = items.find(
                          (item) => item.value === currentValue,
                        );
                        if (selectedItem) {
                          onChange(currentValue, selectedItem);
                        }
                        setOpen(false);
                        setInputValue("");
                      }}
                    >
                      {reactNodeToString(item.label)}
                      <Check
                        className={cn(
                          "ml-auto",
                          value === item.value ? "opacity-100" : "opacity-0",
                        )}
                      />
                    </CommandItem>
                  ))}
                </CommandGroup>

                {showCreateOption && (
                  <CommandGroup heading="Create">
                    <CreateItemComponent
                      input={inputValue}
                      onSelect={handleEnterCreateMode}
                    />
                  </CommandGroup>
                )}
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>

        <Button
          type="button"
          variant="outline"
          className={cn(
            "rounded-l-none border-l-0 px-3",
            isCreatingItem && "rounded-b-none",
          )}
          onClick={handleEnterCreateMode}
          disabled={!onCreateItem}
          aria-label={`Create new ${toTitleCase(itemName)}`}
        >
          <PlusCircle className="h-4 w-4" />
        </Button>
      </div>

      <AnimatePresence initial={false}>
        {isCreatingItem && (
          <motion.section
            key="inline-create-panel"
            initial="collapsed"
            animate="open"
            exit="collapsed"
            variants={panelVariants}
            transition={{ duration: 0.22, ease: "easeInOut" }}
            className={cn(
              "border border-border rounded-t-none -mt-px",
              "bg-secondary/20",
            )}
          >
            <div className="max-h-96 overflow-auto">
              <div className="sticky top-0 z-10 bg-secondary/50 border-b border-border flex items-center justify-between px-2 py-1">
                <div className="text-sm text-secondary-foreground">
                  Create new {toTitleCase(itemName)}
                </div>
                <Button variant="ghost" size="sm" onClick={handleCreateCancel}>
                  <ArrowLeftIcon className="h-4 w-4 mr-1" />
                  Cancel
                </Button>
              </div>
              <div className="p-3">
                <CreateItemFormComponent
                  input={inputValue}
                  onSubmit={handleCreateSubmit}
                  onCancel={handleCreateCancel}
                />
              </div>
            </div>
          </motion.section>
        )}
      </AnimatePresence>
    </div>
  );
}

const reactNodeToString = function (reactNode: React.ReactNode): string {
  let string = "";
  if (typeof reactNode === "string") {
    string = reactNode;
  } else if (typeof reactNode === "number") {
    string = reactNode.toString();
  } else if (reactNode instanceof Array) {
    reactNode.forEach(function (child) {
      string += reactNodeToString(child);
    });
  } else if (
    isValidElement(reactNode) &&
    "props" in reactNode &&
    "children" in (reactNode.props as any)
  ) {
    string += reactNodeToString((reactNode.props as any).children);
  }
  return string;
};
